import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { inject } from 'mobx-react';
import { compose } from 'recompose';
import { withSnackbar } from 'notistack';
import PickupDialog from '../Dispatch/RouteExecution/Pickups/PickupInfoPopup/PickupDialog';
import {
  applyBooleanOrAcrossMembers,
  buildGroupPickupDialogState
} from '../Dispatch/RouteExecution/Pickups/PickupInfoPopup/groupPickupDialogState';
import { AppConfigContext } from '../../../appConfigContext';

type PickInfoState = Record<string, unknown> & { pickupInfo?: unknown };

const ShowGroupPickupInfoPopup = (props: {
  groupId: string;
  DispatchStore: {
    pickupsList: unknown[];
    currentActiveRoutes: unknown;
  };
  enqueueSnackbar: (message: string, options?: { variant?: string }) => void;
}) => {
  const { groupId, DispatchStore, enqueueSnackbar } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const configJSON = useContext(AppConfigContext);
  const [pickInfoStateData, setPickInfoStateData] = useState<PickInfoState | null>(null);

  const handleClose = useCallback(() => {
    setPickInfoStateData(null);
    navigate(location.pathname, { replace: true });
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (!groupId) {
      return undefined;
    }
    // `groupId` from the URL is the source of truth for this route; state may carry the same dialog payload in-memory.
    const normalizedGroupId = String(groupId).toUpperCase();
    if (location.state?.isGroupPickupDialog && location.state.pickupInfo) {
      const members = location.state.groupMemberPickups;
      setPickInfoStateData({
        ...location.state,
        groupId: normalizedGroupId,
        pickupInfo: applyBooleanOrAcrossMembers(location.state.pickupInfo, members)
      });
      return undefined;
    }
    const groupPickupDialogBuiltState = buildGroupPickupDialogState(
      groupId,
      DispatchStore.pickupsList as any[],
      DispatchStore.currentActiveRoutes
    );
    if (!groupPickupDialogBuiltState) {
      enqueueSnackbar('Group pickup details not found', {
        variant: 'error'
      });
      handleClose();
      return undefined;
    }
    setPickInfoStateData({
      isGroupPickupDialog: true,
      groupId: normalizedGroupId,
      pickupInfo: groupPickupDialogBuiltState.pickupInfo,
      groupMemberPickups: groupPickupDialogBuiltState.groupMemberPickups,
      groupBulkEditPickupIds: groupPickupDialogBuiltState.groupBulkEditPickupIds,
      driverDetails: groupPickupDialogBuiltState.driverDetails,
      shipmentAPI: 'GetPickupShipments',
      ...(groupPickupDialogBuiltState.driverDetails || {}),
      isPastPickup: false,
      isPickupHistory: false,
      proImageApiName: 'GetImages'
    });
    return undefined;
    // Intentionally depend on list length / routes so a cold `?groupId=` can populate once dispatch data loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, DispatchStore.pickupsList.length, DispatchStore.currentActiveRoutes]);

  if (!(configJSON as any)?.enableBulkEditAndVoid) {
    return null;
  }

  return groupId && pickInfoStateData?.pickupInfo ? (
    <PickupDialog pickInfoStateData={pickInfoStateData} handleClose={handleClose} />
  ) : null;
};

export default compose(inject('DispatchStore'), withSnackbar)(ShowGroupPickupInfoPopup);
